import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { Check, Loader2, Sparkles } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { create } from 'zustand'

import { isApiError } from '@/api/client'
import {
  cancelJob,
  estimateDeepAnnotate,
  startDeepAnnotate,
  type DeepAnnotateMode,
  type DeepAnnotateRequest,
  type DeepAnnotateResult,
  type DeepEstimate,
} from '@/api/jobs'
import { keys } from '@/api/keys'
import { AUTO_ACCEPT_THRESHOLD, EMPTY_DIFF, getProposal } from '@/api/proposals'
import { JobFailedError, waitForJob } from '@/api/skel'
import { Button } from '@/components/ui/button'
import { Chip } from '@/components/ui/chip'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { toast } from '@/components/ui/toast'
import { cn } from '@/components/ui/utils'
import { formatDuration, formatUsd } from '@/lib/format'
import { isStageBody, normaliseForSave, useEditorStore } from '@/stores/editorStore'

import { reviewStateOf } from './applyProposal'
import { AnnotationModelSelector, useAnnotationModel } from './AnnotationModelSelector'

/**
 * "Mark with AI" — the in-editor deep auto-annotation (`POST /api/annotate/deep`).
 *
 * One click opens a short dialog: what will happen in one sentence, how long and how much it will
 * cost (from the server's estimate), and "More options" (how careful, how many look-again rounds,
 * a spending limit, the model, or measure without AI). Start queues the run; the dialog then shows
 * the steps as they happen with a Stop button, and can be closed — the button keeps showing the
 * progress. When the run is done its suggestions open in the review bar, **on the picture only**:
 * nothing is saved until the person accepts what they want and presses Save.
 *
 * The run gets the picture as it is on screen (unsaved edits included), so it never proposes over
 * anything the person drew, fixed or deleted. It survives switching between the simple and the
 * expert editor (the run lives in a small module store, not in this component).
 */

type Effort = NonNullable<DeepAnnotateRequest['effort']>
type Rounds = 0 | 1 | 2

export interface AutoRun {
  uid: string
  jobId: number
  mode: DeepAnnotateMode
  rounds: Rounds
  status: 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled'
  phase: string | null
  pct: number | null
  stopping?: boolean
  error?: string
}

interface AutoRunsState {
  runs: Record<string, AutoRun>
  put(uid: string, run: AutoRun | null): void
  patch(uid: string, patch: Partial<AutoRun>): void
}

/** Runs by picture uid — outside React so a run survives the shell switching. */
export const useAutoAnnotateRuns = create<AutoRunsState>()((set) => ({
  runs: {},
  put: (uid, run) =>
    set((s) => {
      const runs = { ...s.runs }
      if (run) runs[uid] = run
      else delete runs[uid]
      return { runs }
    }),
  patch: (uid, patch) =>
    set((s) => {
      const cur = s.runs[uid]
      return cur ? { runs: { ...s.runs, [uid]: { ...cur, ...patch } } } : s
    }),
}))

const followers = new Map<string, AbortController>()

/* -------------------------------------------------------------------------- */
/* Steps                                                                       */
/* -------------------------------------------------------------------------- */

export interface RunStep {
  id: 'look' | 'layout' | 'spots' | 'check' | 'critique' | 'ready'
  label: string
}

export function stepsFor(mode: DeepAnnotateMode, rounds: Rounds): RunStep[] {
  const out: RunStep[] = [{ id: 'look', label: 'Looking at the picture' }]
  if (mode === 'heuristic_only') out.push({ id: 'layout', label: 'Measuring ground, water and sky' })
  else out.push({ id: 'layout', label: 'Finding ground, water and sky' }, { id: 'spots', label: 'Finding spots and ways in and out' })
  out.push({ id: 'check', label: 'Checking that everything makes sense' })
  if (mode !== 'heuristic_only' && rounds > 0) out.push({ id: 'critique', label: rounds > 1 ? 'Looking again and fixing mistakes (twice)' : 'Looking again and fixing mistakes' })
  out.push({ id: 'ready', label: 'Getting the suggestions ready' })
  return out
}

/** Which step a job phase belongs to (`verify` runs again inside each look-again round). */
export function stepOfPhase(phase: string | null, pct: number | null): RunStep['id'] {
  switch (phase) {
    case 'layout':
    case 'fallback':
      return 'layout'
    case 'spots':
      return 'spots'
    case 'verify':
      return (pct ?? 0) >= 0.74 ? 'critique' : 'check'
    case 'critique':
    case 'repair':
      return 'critique'
    case 'proposal':
    case 'done':
      return 'ready'
    default:
      return 'look'
  }
}

/* -------------------------------------------------------------------------- */
/* Running                                                                     */
/* -------------------------------------------------------------------------- */

async function showSuggestions(qc: QueryClient, uid: string, jobId: number, result: DeepAnnotateResult): Promise<void> {
  void qc.invalidateQueries({ queryKey: keys.proposals.all() })
  const p = await getProposal(result.proposal_id)
  const st = useEditorStore.getState()
  if (st.meta?.uid !== uid) {
    toast.info('The AI suggestions are ready', { description: 'Open that picture again to check them.' })
    return
  }
  st.setProposal({
    id: p.id,
    assetUid: p.asset_uid,
    body: p.body,
    diff: p.diff ?? EMPTY_DIFF,
    view: 'overlay',
    threshold: AUTO_ACCEPT_THRESHOLD,
    source: result.source ?? p.source,
    mode: result.mode,
    flags: result.flags ?? p.flags,
    findings: result.findings ?? [],
    jobId,
    costUsd: result.cost_usd,
    model: result.model ?? p.model,
    provider: result.provider,
  })
  const after = useEditorStore.getState()
  const n = reviewStateOf(after.doc, after.proposal, []).items.length
  if (n) toast.success(`The AI has ${n} ${n === 1 ? 'suggestion' : 'suggestions'}`, { description: 'Check them above the picture, keep the ones you like, then press Save.' })
  else toast.info('The AI found nothing new', { description: 'Your picture already has everything it found.' })
}

/** Follow a run to its end, keeping the module store up to date; opens the suggestions when done. */
export async function followRun(qc: QueryClient, run: AutoRun): Promise<void> {
  const { put, patch } = useAutoAnnotateRuns.getState()
  followers.get(run.uid)?.abort()
  const ctrl = new AbortController()
  followers.set(run.uid, ctrl)
  put(run.uid, run)
  try {
    const result = await waitForJob<DeepAnnotateResult>(
      run.jobId,
      (u) => patch(run.uid, { status: u.status === 'queued' ? 'queued' : 'running', phase: u.phase, pct: u.pct }),
      ctrl.signal,
      { stream: true, intervalMs: 1500 },
    )
    patch(run.uid, { status: 'succeeded', phase: 'done', pct: 1 })
    try {
      if (!result || typeof result.proposal_id !== 'number') throw new Error('The run finished without suggestions.')
      await showSuggestions(qc, run.uid, run.jobId, result)
    } catch (err) {
      // The run is over either way; say so instead of leaving the person waiting for a bar that never comes.
      toast.error('The AI suggestions could not be opened', { description: err instanceof Error ? err.message : undefined })
    } finally {
      put(run.uid, null)
    }
  } catch (err) {
    if (ctrl.signal.aborted) return
    if (err instanceof JobFailedError && err.status === 'cancelled') patch(run.uid, { status: 'cancelled', stopping: false })
    else {
      const message = err instanceof Error ? err.message : 'The run did not finish.'
      patch(run.uid, { status: 'failed', stopping: false, error: message })
      // The dialog may be closed ("Keep working meanwhile"): the button alone would just go quiet.
      toast.error('The AI could not finish marking this picture', { description: 'Nothing on your picture changed. Open "Mark with AI" to try again.' })
    }
  } finally {
    if (followers.get(run.uid) === ctrl) followers.delete(run.uid)
  }
}

/** The server refuses a `context_body` above 256 KiB of UTF-8 (`413`). */
export const MAX_CONTEXT_BYTES = 262_144

/**
 * The picture as the request carries it. Only unsaved work needs sending — with nothing unsaved the
 * server's saved copy IS the picture on screen, and it uses that when this is left out. `too_big`
 * when unsaved work would not fit (measured in UTF-8 bytes, like the server: a label in Cyrillic
 * is two bytes a letter).
 */
export function contextBody(): { body?: DeepAnnotateRequest['context_body']; tooBig?: true } {
  const st = useEditorStore.getState()
  if (!st.dirty || !isStageBody(st.doc)) return {}
  const body = normaliseForSave(st.doc)
  try {
    const bytes = new TextEncoder().encode(JSON.stringify(body)).length
    return bytes <= MAX_CONTEXT_BYTES - 1024 ? { body } : { tooBig: true }
  } catch {
    return {}
  }
}

/* -------------------------------------------------------------------------- */
/* Component                                                                   */
/* -------------------------------------------------------------------------- */

const EFFORTS: { value: Effort; label: string }[] = [
  { value: 'low', label: 'Quick look' },
  { value: 'medium', label: 'Normal' },
  { value: 'high', label: 'Careful' },
]

const ROUNDS: { value: Rounds; label: string }[] = [
  { value: 0, label: 'No' },
  { value: 1, label: 'Once' },
  { value: 2, label: 'Twice' },
]

function clock(iso: string | undefined): string {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  } catch {
    return ''
  }
}

function estimateLine(e: DeepEstimate | undefined, heuristicOnly: boolean): string | null {
  if (!e) return null
  if (heuristicOnly || e.mode === 'heuristic_only') return 'Free, and takes a few seconds.'
  const time = `Usually takes about ${formatDuration(Math.max(30, e.seconds))}`
  if (e.mode === 'mock') return `${time}. Free in test mode.`
  const cost = e.usd !== null ? `costs about ${formatUsd(e.usd)} (never more than ${formatUsd(e.cap_usd)})` : `costs at most ${formatUsd(e.cap_usd)}`
  return `${time} and ${cost}.`
}

export interface AutoAnnotateButtonProps {
  className?: string
  /** Button label (default "Mark with AI"). */
  label?: string
}

export function AutoAnnotateButton({ className, label = 'Mark with AI' }: AutoAnnotateButtonProps = {}) {
  const qc = useQueryClient()
  const uid = useEditorStore((s) => s.meta?.uid ?? null)
  const isStage = useEditorStore((s) => isStageBody(s.doc))
  const dirty = useEditorStore((s) => s.dirty)
  const run = useAutoAnnotateRuns((s) => (uid ? s.runs[uid] : undefined))
  const [open, setOpen] = useState(false)
  const [more, setMore] = useState(false)
  const [effort, setEffort] = useState<Effort>('medium')
  const [rounds, setRounds] = useState<Rounds>(1)
  const aiModel = useAnnotationModel(open)
  const [budget, setBudget] = useState('')
  const [heuristicOnly, setHeuristicOnly] = useState(false)
  const [starting, setStarting] = useState(false)
  const [startError, setStartError] = useState<string | null>(null)

  const active = run && (run.status === 'queued' || run.status === 'running')
  const budgetUsd = budget.trim() === '' ? undefined : Number(budget)
  const budgetInvalid = budgetUsd !== undefined && (!Number.isFinite(budgetUsd) || budgetUsd < 0.05 || budgetUsd > 20)

  const estimate = useQuery({
    queryKey: ['annotate', 'deep', 'estimate', uid, aiModel.selection, effort, rounds, budgetInvalid ? null : (budgetUsd ?? null), heuristicOnly],
    queryFn: ({ signal }) =>
      estimateDeepAnnotate(
        { uid: uid!, ...(!heuristicOnly && aiModel.selection ? aiModel.selection : {}), effort, critique_rounds: rounds, heuristic_only: heuristicOnly, ...(budgetUsd !== undefined && !budgetInvalid ? { budget_usd: budgetUsd } : {}) },
        signal,
      ),
    enabled: open && !!uid && isStage && !active && (heuristicOnly || aiModel.ready),
    staleTime: 60_000,
    retry: 0,
  })

  // A finished run hands over to the review bar: close the dialog it was shown in.
  const wasActive = useRef(false)
  useEffect(() => {
    if (active) wasActive.current = true
    else if (wasActive.current && !run) {
      wasActive.current = false
      setOpen(false)
    }
  }, [active, run])

  if (!uid || !isStage) return null

  const start = async () => {
    if (!heuristicOnly && !aiModel.ready) return
    setStarting(true)
    setStartError(null)
    try {
      const body: DeepAnnotateRequest = {
        uid,
        ...(!heuristicOnly && aiModel.selection ? aiModel.selection : {}),
        effort,
        critique_rounds: rounds,
        ...(heuristicOnly ? { heuristic_only: true } : {}),
        ...(budgetUsd !== undefined && !budgetInvalid ? { budget_usd: budgetUsd } : {}),
      }
      const ctx = contextBody()
      if (ctx.tooBig) {
        setStartError('This picture has too many unsaved marks to send along. Press Save first, then try again.')
        return
      }
      if (ctx.body) body.context_body = ctx.body
      const meta = useEditorStore.getState().meta
      if (meta) body.base_rev = meta.rev
      const res = await startDeepAnnotate(body)
      void followRun(qc, { uid, jobId: res.job_id, mode: res.mode, rounds, status: 'queued', phase: null, pct: null })
    } catch (err) {
      if (isApiError(err) && err.status === 409 && Number(err.details['existing_job_id'])) {
        // Already being marked (another tab, or a library run): follow that one.
        void followRun(qc, { uid, jobId: Number(err.details['existing_job_id']), mode: 'ai', rounds, status: 'running', phase: null, pct: null })
      } else if (isApiError(err) && err.status === 413) {
        setStartError('This picture has too many unsaved marks to send along. Press Save first, then try again.')
      } else {
        setStartError(err instanceof Error ? err.message : 'The AI could not be started.')
      }
    } finally {
      setStarting(false)
    }
  }

  const stop = async () => {
    if (!run) return
    useAutoAnnotateRuns.getState().patch(uid, { stopping: true })
    try {
      await cancelJob(run.jobId)
    } catch (err) {
      useAutoAnnotateRuns.getState().patch(uid, { stopping: false })
      toast.error('Could not stop the AI', { description: err instanceof Error ? err.message : undefined })
    }
  }

  const reset = () => {
    useAutoAnnotateRuns.getState().put(uid, null)
    setStartError(null)
  }

  const e = estimate.data
  const mode: DeepAnnotateMode = heuristicOnly ? 'heuristic_only' : (e?.mode ?? 'ai')
  const pctLabel = run?.pct != null ? ` ${Math.round(run.pct * 100)}%` : ''

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        className={className}
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
        data-testid="auto-annotate"
      >
        {active ? <Loader2 className="animate-spin" aria-hidden /> : <Sparkles aria-hidden />}
        {active ? (run.status === 'queued' ? 'AI is waiting…' : `AI is marking…${pctLabel}`) : label}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>{active ? 'The AI is marking this picture' : 'Let the AI mark this picture'}</DialogTitle>
            <DialogDescription>
              The AI looks at the picture and suggests where characters can walk, swim, hide, sit, and come in or out. You check every suggestion — nothing changes until you
              accept it and press Save.
            </DialogDescription>
          </DialogHeader>

          {run && run.status !== 'succeeded' ? (
            <RunProgress run={run} />
          ) : (
            <div className="grid gap-3 text-sm">
              <AnnotationModelSelector model={aiModel} disabled={heuristicOnly || starting} />
              {estimate.isLoading ? <p className="text-ink-2">Working out how long it takes…</p> : null}
              {estimate.isError && !heuristicOnly ? <p className="text-ink-2">Could not work out the time and cost just now. The run never spends more than its limit.</p> : null}
              {estimateLine(e, heuristicOnly) ? <p className="text-ink">{estimateLine(e, heuristicOnly)}</p> : null}
              {e?.mode === 'mock' && !heuristicOnly ? (
                <p className="rounded-lg bg-info-soft px-3 py-2 text-ink">Test mode: the AI is not connected, so you will get made-up example suggestions.</p>
              ) : null}
              {e?.mode === 'heuristic_only' && !heuristicOnly ? (
                <p className="rounded-lg bg-warn-soft px-3 py-2 text-ink">
                  The AI is not available right now, so the studio will measure the picture itself. It is free but simpler: fewer spots and ways in and out.
                </p>
              ) : null}
              {e?.paused_until && mode !== 'heuristic_only' ? (
                <p className="rounded-lg bg-warn-soft px-3 py-2 text-ink">The AI is taking a break until {clock(e.paused_until)}. Your request will wait its turn.</p>
              ) : null}
              {dirty ? <p className="text-ink-2">Your unsaved changes are included. Anything you drew or fixed yourself is kept as it is.</p> : <p className="text-ink-2">Anything you drew or fixed yourself is kept as it is.</p>}

              <div>
                <Button variant="link" size="sm" className="h-auto px-0" aria-expanded={more} onClick={() => setMore((v) => !v)}>
                  {more ? 'Fewer options' : 'More options'}
                </Button>
              </div>
              {more ? (
                <div className="grid gap-3 rounded-xl border border-line p-3">
                  <OptionRow label="How careful">
                    {EFFORTS.map((o) => (
                      <Chip key={o.value} size="sm" selected={effort === o.value} onClick={() => setEffort(o.value)} disabled={heuristicOnly}>
                        {o.label}
                      </Chip>
                    ))}
                  </OptionRow>
                  <OptionRow label="Look again and fix mistakes">
                    {ROUNDS.map((o) => (
                      <Chip key={o.value} size="sm" selected={rounds === o.value} onClick={() => setRounds(o.value)} disabled={heuristicOnly}>
                        {o.label}
                      </Chip>
                    ))}
                  </OptionRow>
                  <label className="flex flex-wrap items-center gap-2 text-sm text-ink">
                    <span className="min-w-40">Spend at most ($)</span>
                    <Input
                      type="number"
                      inputMode="decimal"
                      min={0.05}
                      max={20}
                      step={0.05}
                      placeholder={e ? e.cap_usd.toFixed(2) : '1.50'}
                      value={budget}
                      onChange={(ev) => setBudget(ev.target.value)}
                      className="w-28"
                      disabled={heuristicOnly}
                      aria-invalid={budgetInvalid || undefined}
                    />
                    {budgetInvalid ? <span className="text-xs text-danger">Between $0.05 and $20.</span> : null}
                  </label>
                  <label className="flex items-center gap-2 text-sm text-ink">
                    <input type="checkbox" checked={heuristicOnly} onChange={(ev) => setHeuristicOnly(ev.target.checked)} />
                    Measure without AI (free, simpler)
                  </label>
                </div>
              ) : null}

              {startError ? (
                <p role="alert" className="rounded-lg bg-danger-soft px-3 py-2 text-ink">
                  {startError}
                </p>
              ) : null}
            </div>
          )}

          <DialogFooter>
            {active ? (
              <>
                <Button variant="ghost" onClick={() => void stop()} disabled={run.stopping}>
                  {run.stopping ? 'Stopping…' : 'Stop'}
                </Button>
                <Button variant="primary" onClick={() => setOpen(false)}>
                  Keep working meanwhile
                </Button>
              </>
            ) : run && (run.status === 'failed' || run.status === 'cancelled') ? (
              <>
                <Button variant="ghost" onClick={() => setOpen(false)}>
                  Close
                </Button>
                <Button variant="primary" onClick={reset}>
                  Try again
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button variant="primary" loading={starting} disabled={budgetInvalid || (!heuristicOnly && !aiModel.ready)} onClick={() => void start()}>
                  {mode === 'heuristic_only' ? 'Measure now' : 'Start'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function OptionRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-2" role="group" aria-label={label}>
      <span className="min-w-40 text-sm text-ink">{label}</span>
      {children}
    </div>
  )
}

/** The live steps strip: done ✓ · current (spinner) · still to come. */
export function RunProgress({ run }: { run: AutoRun }) {
  const steps = stepsFor(run.mode, run.rounds)
  const current = stepOfPhase(run.phase, run.pct)
  const at = Math.max(0, steps.findIndex((s) => s.id === current))
  const finished = run.status === 'succeeded'
  const pct = Math.round(Math.min(1, Math.max(0, run.pct ?? 0)) * 100)
  return (
    <div className="grid gap-3 text-sm" data-testid="auto-annotate-progress">
      {run.status === 'queued' ? <p className="text-ink-2">Waiting for a turn — another picture is being marked first.</p> : null}
      {run.status === 'failed' ? (
        <p role="alert" className="rounded-lg bg-danger-soft px-3 py-2 text-ink">
          Something went wrong: {run.error ?? 'the run did not finish.'} Nothing on your picture changed.
        </p>
      ) : null}
      {run.status === 'cancelled' ? <p className="text-ink-2">Stopped. Nothing on your picture changed.</p> : null}
      {run.status === 'queued' || run.status === 'running' ? (
        <>
          <div className="h-2 overflow-hidden rounded-full bg-paper-3" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label="How far the AI is">
            <div className="h-full rounded-full bg-accent transition-[width]" style={{ width: `${pct}%` }} />
          </div>
          <ol className="grid gap-1.5" aria-label="Steps">
            {steps.map((s, i) => {
              const state = finished || i < at ? 'done' : i === at && run.status === 'running' ? 'current' : 'todo'
              return (
                <li key={s.id} className={cn('flex items-center gap-2', state === 'todo' ? 'text-ink-3' : 'text-ink')} data-state={state}>
                  {state === 'done' ? (
                    <Check className="size-4 text-ok" aria-hidden />
                  ) : state === 'current' ? (
                    <Loader2 className="size-4 animate-spin text-accent" aria-hidden />
                  ) : (
                    <span className="inline-block size-4 rounded-full border border-line-strong" aria-hidden />
                  )}
                  <span className={state === 'current' ? 'font-medium' : undefined}>{s.label}</span>
                </li>
              )
            })}
          </ol>
          {run.stopping ? <p className="text-xs text-ink-2">Stopping after the current step…</p> : null}
        </>
      ) : null}
    </div>
  )
}
